import type { Eta } from '../eta/eta.interface';
import type { BaseDocument } from '../mongo/base-document.interface';
import type { Order, TimeSlot } from '../order/order.interface';
import type { RoutingVehicleProfile } from '../routing/routing.interface';
import type { Task, TaskJob, TaskJobType } from '../task/task.interface';
import type { UserComment } from '../user-comment/user-comment.interface';

export const STOPS_QUEUE_STATUS = {
  ONGOING: 'ongoing',
} as const;

export type StopsQueueStatus = (typeof STOPS_QUEUE_STATUS)[keyof typeof STOPS_QUEUE_STATUS];

/**
 * A single job grouped into a stop — a flattened summary of the task job it belongs to (the
 * TaskJob's own `id`, `type`, and `sortIndex`), plus the `taskId`/`orderId` it references.
 */
export interface StopJob {
  id: string;
  orderId: string;
  sortIndex: number;
  taskId: string;
  type: TaskJobType;
}

/**
 * A stop is a visit that groups one or more jobs (e.g. multiple pickups at the same
 * location). `id` is a stable, generated identifier for the stop subdocument. `fullAddress`
 * and `timeSlot` are denormalized from the stop's single source job at insert time (see
 * `buildStop()` in `apps/pharos-back-deliveries/src/stops-queue/queue-ordering.ts`) so
 * `insertTaskStops` can order the queue without re-fetching the task/job documents.
 */
export interface Stop {
  /** Stamped by the ETA engine (`pharos-back-deliveries`) whenever the queue or its execution state changes. */
  eta?: Eta;
  fullAddress: string;
  id: string;
  /**
   * The first ETA the engine ever stamped on this stop — write-once, never overwritten once set.
   * Kept for ETA-accuracy measurement against the stop's actual arrival; `eta` keeps updating with
   * every recompute.
   */
  initialEta?: Eta;
  jobs: StopJob[];
  /**
   * Neutral encoded geometry (Google encoded polyline, precision 5) of the leg leading to this
   * stop — from the previous stop, or from the ETA anchor for the first remaining stop. Stamped
   * by the ETA engine alongside `eta`; absent when the last computation produced no geometry for
   * this leg.
   */
  routePolyline?: string;
  timeSlot?: TimeSlot;
  /** Canonical driver comment for this stop's address, stamped at insert time (see insertTaskStops). */
  userComment?: UserComment;
}

/**
 * A hydrated job: the raw `StopJob` reference resolved to its full `TaskJob`, `Task`, and
 * `Order`. Used by the read path of GET /queue.
 */
export interface EnrichedStopJob {
  order: Order;
  task: Task;
  taskJob: TaskJob;
}

/**
 * A hydrated stop: the persisted stop's `id` plus its surviving hydrated jobs. `fullAddress`
 * and `timeSlot` are copied from the persisted `Stop`.
 */
export interface EnrichedStop {
  fullAddress: string;
  id: string;
  jobs: EnrichedStopJob[];
  /** Decoded `routePolyline`, as `[lng, lat]` points (MapLibre order). Absent when no geometry is stored. */
  route?: [number, number][];
  timeSlot?: TimeSlot;
  userComment?: UserComment;
}

/**
 * The driver's persisted ongoing queue.
 * The `stops` array is ordered: array order IS the execution order.
 */
export interface StopsQueue extends BaseDocument {
  carrierId: string;
  driverId: string;
  /**
   * Set when the driver manually reorders the queue (`PATCH /queue/reorder`). When present,
   * newly inserted stops append at the end of the queue instead of timeslot-insertion. Absent
   * means the queue was never manually reordered.
   */
  reorderedAt?: Date;
  status: StopsQueueStatus;
  stops: Stop[];
  /** Stamped lazily on first touch after creation (see `VehicleProfileStamperService`). Absent means resolution failed or hasn't run yet — the routing request falls back to a lightweight-vehicle profile. */
  vehicleProfile?: RoutingVehicleProfile;
}

/** Response of `PATCH /queue/reorder`: the hydrated queue plus any pickup stop ids the server had to reposition. */
export interface ReorderQueueResponse {
  correctedStopIds: string[];
  stops: EnrichedStop[];
}

export const STOP_COMMENT_TARGET_KIND = {
  CUSTOMER_ADDRESS: 'customerAddress',
  SITE: 'site',
} as const;
export type StopCommentTargetKind = (typeof STOP_COMMENT_TARGET_KIND)[keyof typeof STOP_COMMENT_TARGET_KIND];

/** Where a stop's driver comment canonically lives, resolved from the order + job type. */
export type StopCommentTarget =
  | { customerId: string; kind: typeof STOP_COMMENT_TARGET_KIND.CUSTOMER_ADDRESS; originalAddress: string }
  | { kind: typeof STOP_COMMENT_TARGET_KIND.SITE; siteId: string };
